import 'devextreme/dist/css/dx.common.css';
import './themes/generated/dx.material.base.css';
import './assets/icons/style.scss';
import { HashRouter as Router } from 'react-router-dom';
import './css/dx-styles.scss';
import LoadPanel from 'devextreme-react/load-panel';
import { NavigationProvider } from './contexts/navigation';
import { OutageBanner } from './components/outage-banner';
import { OutagePage } from './pages/outage-page';
import { OutageConfig, useGetOutageConfig } from './api/get-outage-config';
import { AuthProvider, useAuth } from './contexts/auth';
import { useScreenSizeClass } from './utils/media-query';
import Content from './Content';
import UnauthenticatedContent from './UnauthenticatedContent';
import { queryClient } from './lib/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TitleProvider } from "./contexts/title";
import { hotjar } from 'react-hotjar';
import { useEffect, useRef, useState } from 'react';
import { HOTJAR_ID, HOTJAR_VERSION } from './config/constants';
import config from "devextreme/core/config"
import { getDynatraceRumTag } from './api/dynatrace';
import { useGetBuildInformation } from './api/build';
import { BUILD_NUMBER } from './build-number';
import { createDynatraceInitializer, endDynatraceSession, identifyDynatraceUser } from './utils/dynatrace';

const initializeMonitoring = createDynatraceInitializer();

function AppContent({ outageConfig }: { outageConfig?: OutageConfig }) {
	const { user, loading } = useAuth();
	const [dynatraceReady, setDynatraceReady] = useState(false);
	const identifiedDynatraceUser = useRef<string>();

	useEffect(() => {
		let mounted = true;
		const initialization = initializeMonitoring(() => {
			const dynatraceRumTag = getDynatraceRumTag();
			if (dynatraceRumTag) {
				hotjar.initialize(HOTJAR_ID, HOTJAR_VERSION);
			}
			return dynatraceRumTag;
		}, injectScriptTag);
		initialization
			?.then(() => {
				if (mounted) {
					setDynatraceReady(true);
				}
			})
			.catch(() => undefined);
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (!dynatraceReady) {
			return;
		}

		const userName = user?.userName;
		if (userName) {
			if (identifiedDynatraceUser.current && identifiedDynatraceUser.current !== userName) {
				endDynatraceSession();
			}
			if (identifyDynatraceUser(userName)) {
				identifiedDynatraceUser.current = userName;
			}
		} else if (identifiedDynatraceUser.current && endDynatraceSession()) {
			identifiedDynatraceUser.current = undefined;
		}
	}, [dynatraceReady, user]);

	const buildInfo = useGetBuildInformation();
	useEffect(() => {
		if (buildInfo.data && buildInfo.data?.buildNumber !== BUILD_NUMBER) {
			window.location.reload();
		}
	}, [buildInfo.data?.buildNumber]);

	if (loading) {
		return <LoadPanel visible={true} />;
	}

	return (
		<>
			<OutageBanner config={outageConfig} />
			{user ? <Content /> : <UnauthenticatedContent />}
		</>
	);
}

function App() {
	const { data: outageConfig } = useGetOutageConfig();
	const screenSizeClass = useScreenSizeClass();

	config({ licenseKey: 'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogImRjNmNmNGUxLTJmYmYtMTFlNS04MGNjLTAwMTU1ZDYyNDcwMSIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjMyCn0=.PaUzGnM9FIk/bgNIGI2Fd2v482mrdWTcNWRt6bNuV+AVsbvXb031EQzNvnWC5gTaQ01tdFyHL2h//yr8LwbQSHMAqt2OzMm0830eSRbvxKOlByHvMbcVO0z4EX3LjowXL6JJcA==' });

	if (outageConfig?.outagePageActive) {
		return <OutagePage config={outageConfig} appName="Sage Sync" />;
	}

	return (
		<Router>
			<AuthProvider>
				<NavigationProvider>
					<TitleProvider>
						<ReactQueryDevtools initialIsOpen={false} position={"bottom-right"} />
						<div className={`app ${screenSizeClass}`}>
							<AppContent outageConfig={outageConfig} />
						</div>
					</TitleProvider>
				</NavigationProvider>
			</AuthProvider>
		</Router>
	);
}

export default function Root() {
	return (
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>
	);
}

function injectScriptTag(scriptText: string, onLoad: () => void, onError: (error?: unknown) => void) {
	const scriptTag = document.createElement('script');
	let scriptUrl = scriptText.substring(scriptText.indexOf('src=') + 5, scriptText.indexOf('.js') + 3)
	scriptTag.type = 'text/javascript';
	scriptTag.src = scriptUrl;
	scriptTag.crossOrigin = 'anonymous';
	scriptTag.onload = onLoad;
	scriptTag.onerror = onError;
	document.head.appendChild(scriptTag);
}
